import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ProviderError } from '@recto/provider';

import { getEngine } from '../app/engine';
import { getDeviceProvider } from '../app/providers';
import { useDocumentStore } from '../stores/useDocumentStore';

const PDF_MIME_FILTER = {
  description: 'PDFs',
  mimeTypes: ['application/pdf'] as const,
  extensions: ['.pdf'] as const,
};

// Monotonic token identifying the most-recently-issued open call.
// If a second open starts before the first `engine.load` resolves,
// the older token loses; the loaded PDF is released so its
// PDFDocumentProxy + worker connection don't leak. Module-scope so
// every `useDeviceStorage` consumer shares the same in-flight state.
let openToken = 0;

/**
 * High-level device-storage flows for the Guest track:
 *
 * - `openViaPicker` — triggers `DeviceStorageProvider.pick`, then
 *   loads bytes into the app engine + navigates to /edit.
 * - `openFromDroppedFile` — same terminal steps, entered from a
 *   drag-and-drop `File` (skips the picker).
 * - `saveToSource` — engine.save → provider.upload; toast per
 *   `UploadResult.mode` (`'in-place'` → "Saved"; `'download'` →
 *   "Downloaded to your Downloads folder").
 *
 * Errors bubble to the caller via Sonner toasts; the hook never
 * throws to render.
 */
export function useDeviceStorage(): {
  readonly openViaPicker: () => Promise<void>;
  readonly openFromDroppedFile: (file: File) => Promise<void>;
  readonly saveToSource: () => Promise<void>;
} {
  const navigate = useNavigate();
  const setOpenedDoc = useDocumentStore((s) => s.setOpenedDoc);
  const clearDirty = useDocumentStore((s) => s.clearDirty);

  const openFromRef = useCallback(
    async (ref: Awaited<ReturnType<ReturnType<typeof getDeviceProvider>['pick']>>) => {
      if (ref === null) {
        return; // user cancelled picker
      }
      openToken += 1;
      const myToken = openToken;
      try {
        const provider = getDeviceProvider();
        const engine = await getEngine();
        const { bytes, meta } = await provider.download(ref);
        const pdf = await engine.load(bytes);
        if (myToken !== openToken) {
          // A newer open superseded this one mid-flight — release the
          // orphaned PDFDocument so we don't leak the worker.
          await engine.close(pdf).catch(() => {
            /* close errors on orphan are ignorable */
          });
          return;
        }
        setOpenedDoc({ pdf, sourceRef: ref, originalName: meta.name });
        navigate('/edit');
      } catch (err) {
        // Only surface the error for the current-latest attempt;
        // stale attempts fail silently since their user-visible
        // context is gone.
        if (myToken === openToken) {
          toast.error(userMessage(err, 'Could not open PDF.'));
        }
      }
    },
    [navigate, setOpenedDoc],
  );

  const openViaPicker = useCallback(async (): Promise<void> => {
    try {
      const provider = getDeviceProvider();
      const ref = await provider.pick(PDF_MIME_FILTER);
      await openFromRef(ref);
    } catch (err) {
      toast.error(userMessage(err, 'Could not open PDF.'));
    }
  }, [openFromRef]);

  const openFromDroppedFile = useCallback(
    async (file: File): Promise<void> => {
      if (file.type && file.type !== 'application/pdf') {
        toast.error('Only PDF files are supported.');
        return;
      }
      const provider = getDeviceProvider();
      const ref = provider.wrapFile(file);
      await openFromRef(ref);
    },
    [openFromRef],
  );

  const saveToSource = useCallback(async (): Promise<void> => {
    const { pdf, sourceRef, originalName } = useDocumentStore.getState();
    if (!pdf || !sourceRef) {
      return;
    }
    try {
      const provider = getDeviceProvider();
      const engine = await getEngine();
      const bytes = await engine.save(pdf);
      const result = await provider.upload(sourceRef, bytes);
      const name = originalName ?? 'document.pdf';
      if (result.mode === 'in-place') {
        toast.success(`Saved to ${name}`);
      } else {
        toast.success(`Downloaded to your Downloads folder as ${name}`);
      }
      clearDirty();
    } catch (err) {
      toast.error(userMessage(err, 'Save failed.'));
    }
  }, [clearDirty]);

  return { openViaPicker, openFromDroppedFile, saveToSource };
}

function userMessage(err: unknown, fallback: string): string {
  if (err instanceof ProviderError) {
    // For M1 the provider error messages are already user-safe (no
    // stack traces or internal identifiers); surface directly.
    return err.message;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  return fallback;
}
